// API utility functions for the Dope Chain Wars frontend

import { debugLog } from './debug';
import useGameState from '../engine/gameState';
import * as authService from '../engine/auth/authService';

/**
 * Production-optimized logging function
 * Only logs in development or when explicitly enabled
 */
function apiLog(level, message, ...args) {
  const isProduction = import.meta.env.PROD;
  const enableApiLogs = import.meta.env.VITE_ENABLE_API_LOGS === 'true';

  // In production, only log errors and warnings, never info/debug logs
  if (isProduction && !enableApiLogs) {
    if (level === 'error' || level === 'warn') {
      console[level](`[API] ${message}`, ...args);
    }
    return;
  }

  // In development, log everything
  console[level](`[API] ${message}`, ...args);
}

/**
 * Build API URL based on environment
 */
export function buildApiUrl(path) {
  const base = getApiBase();
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  // In development, use empty string to leverage Vite proxy
  if (!base) {
    return normalizedPath;
  }

  return `${base}${normalizedPath}`;
}

/**
 * Get API base URL
 */
export function getApiBase() {
  return import.meta.env.VITE_BACKEND_URL || '';
}

// Get API key from auth service
// ⚠️ DEPRECATED: Use authService.getToken directly
export function getApiKey() {
  return authService.getToken();
}

// Get API key specifically optimized for chat endpoints
export function getChatApiKey() {
  if (typeof window === 'undefined') return null;

  // Prefer the dedicated chat API key if available (more reliable for chat)
  const chatApiKey = localStorage.getItem('dcw_chat_api_key');
  if (chatApiKey) {
    apiLog('log', 'Using dedicated chat API key');
    return chatApiKey;
  }

  // Fallback to the main API key
  const mainApiKey = authService.getToken();
  if (mainApiKey) {
    apiLog('log', 'Using main API key for chat');
    return mainApiKey;
  }

  return null;
}

// Set chat API key in local storage
export function setChatApiKey(key) {
  if (typeof window === 'undefined') return;
  if (key) {
    localStorage.setItem('dcw_chat_api_key', key);
    apiLog('log', 'Chat API key stored');
  } else {
    localStorage.removeItem('dcw_chat_api_key');
    apiLog('log', 'Chat API key cleared');
  }
}

// Set API key in local storage
// ⚠️ DEPRECATED: Use authService.setToken instead
export function setApiKey(key, address = null) {
  authService.setToken(key);
  if (address) {
    authService.setAuthWallet(address);
  }
}

// Get authenticated wallet from auth service
export function getAuthWallet() {
  return authService.getAuthWallet();
}

// Set authenticated wallet in auth service
export function setAuthWallet(wallet) {
  authService.setAuthWallet(wallet);
}

// DEPRECATED: Use getNonce from ./auth module instead
export async function fetchNoncePayload(base, address) {
  return authService.getNonce(address);
}

// Perform login with signature
export async function performLogin(base, address, signature, nonce, message, sigType = null, extraData = {}) {
  // Delegate to AuthService
  const result = await authService.signIn(signature, message, address, extraData);
  
  // Store session data (for backwards compatibility with existing flows that expect side-effects)
  if (result.api_key) {
    setApiKey(result.api_key);
    
    // Also store refresh token if provided
    if (result.refresh_token) {
      localStorage.setItem('dcw_refresh_token', result.refresh_token);
    }
  }
  
  if (address) setAuthWallet(address);
  
  return result;
}

// Clear authentication data
export function clearAuth() {
  authService.clearSession();
}

// Clear API key (alias for compatibility)
export function clearApiKey() {
  authService.setToken(null);
}

// Invalidate authentication
export function invalidateAuth() {
  clearAuth();
}

// Token refresh utility removed - using simple session invalidation instead

// Fetch with retry and authentication
export async function authFetchWithRetry(url, options = {}, retries = 3) {
  const apiKey = getApiKey() || getChatApiKey();

  if (!apiKey) {
    // If there's no API key, we can't make an authenticated request.
    // This is a critical error in flows that expect authentication.
    console.error(`[API] authFetchWithRetry failed: No API key available for request to ${url}`);
    throw new Error('No API key available for authenticated request');
  }

  // Deep clone options to avoid mutation issues
  const authOptions = JSON.parse(JSON.stringify(options));

  // Ensure headers object exists
  if (!authOptions.headers) {
    authOptions.headers = {};
  }

  // Add Authorization and Content-Type headers
  authOptions.headers['Authorization'] = `Bearer ${apiKey}`;
  if (!authOptions.headers['Content-Type'] && authOptions.body) {
    authOptions.headers['Content-Type'] = 'application/json';
  }

  // Ensure mode is set to 'cors' and credentials to 'include'
  authOptions.mode = 'cors';
  authOptions.credentials = 'include';

  // Prevent caching of API requests
  authOptions.cache = 'no-cache';

  let lastError;
  for (let i = 0; i < retries; i++) {
    try {
      console.log(`[API] Making authenticated request to: ${url}`, authOptions);
      let response = await fetch(url, authOptions);

      // 401: Try /auth/refresh with dcw_refresh_token before invalidating session.
      // This prevents Base/desktop from constantly asking for new signatures.
      if (response.status === 401) {
        const refreshToken = typeof window !== 'undefined' ? localStorage.getItem('dcw_refresh_token') : null;
        if (refreshToken && i === 0) {
          try {
            const refreshUrl = buildApiUrl('/auth/refresh');
            const refreshRes = await fetch(refreshUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ refresh_token: refreshToken }),
              mode: 'cors',
              credentials: 'include'
            });
            if (refreshRes.ok) {
              const tokens = await refreshRes.json();
              if (tokens.access_token) {
                // Refresh only the short-lived JWT. The long-lived API key stays
                // the primary bearer and must not be overwritten here.
                authService.setAccessToken(tokens.access_token);
                if (tokens.refresh_token) localStorage.setItem('dcw_refresh_token', tokens.refresh_token);
                authOptions.headers['Authorization'] = `Bearer ${tokens.access_token}`;
                console.log('[API] Session refreshed via refresh token, retrying request');
                continue; // Retry with new token
              }
            }
          } catch (refreshErr) {
            console.warn('[API] Refresh failed:', refreshErr?.message);
          }
        }

        let errorDetail = 'Unknown authentication error';
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || errorData.message || 'Invalid API key';
        } catch (e) { /* ignore */ }

        console.log(`[API] Invalidating session due to 401: ${errorDetail}`);
        clearAuth();

        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('dcw-auth-invalid', {
            detail: { reason: 'api_key_invalid', message: 'Please log in again.', originalError: errorDetail }
          }));
          window.dispatchEvent(new CustomEvent('dcw-api-key-invalid'));
        }
        break;
      }

      if (!response.ok) {
        let errorData;
        try {
          errorData = await response.json();
        } catch (e) {
          errorData = { message: response.statusText || 'Unknown error' };
        }
        const errorMessage = errorData.detail || errorData.message || `HTTP error ${response.status}`;
        console.error(`[API] Auth fetch failed (Attempt ${i + 1}/${retries}): ${errorMessage}`);
        throw new Error(errorMessage);
      }
      return response; // Success
    } catch (error) {
      lastError = error;
      if (i < retries - 1) {
        // Wait before retrying
        await new Promise(r => setTimeout(r, 1000 * (i + 1)));
      }
    }
  }

  // If all retries fail, throw the last captured error
  console.error(`[API] All auth fetch retries failed for ${url}. Last error:`, lastError);
  throw lastError;
}

// Helper for API POST requests
export async function apiPost(endpoint, data) {
  const url = endpoint.startsWith('http') ? endpoint : buildApiUrl(endpoint);
  const response = await authFetchWithRetry(url, {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.json();
}

// Helper for API GET requests
export async function apiGet(endpoint, options = {}) {
  const url = endpoint.startsWith('http') ? endpoint : buildApiUrl(endpoint);
  const response = await authFetchWithRetry(url, options);
  return response.json();
}

// Helper for WebSocket connections
export function apiWs(endpoint) {
  // If endpoint is absolute, use it. Otherwise construct from base.
  let url;
  if (endpoint.startsWith('ws')) {
    url = endpoint;
  } else {
    const apiBase = getApiBase();
    const protocol = apiBase.startsWith('https') || window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = apiBase.replace(/^http(s)?:\/\//, '') || window.location.host;
    const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    url = `${protocol}//${host}${path}`;
  }
  
  // Attach auth token if available
  const token = authService.getToken();
  if (token) {
    const separator = url.includes('?') ? '&' : '?';
    url = `${url}${separator}token=${token}`;
  }
  
  return new WebSocket(url);
}

// Exchange a third-party token for a DCW token
export async function exchangeToken(provider, token, extra = {}) {
  // CRITICAL: Use the central URL builder for consistency
  const exchangePath = '/auth/exchange';
  const url = buildApiUrl(exchangePath);

  console.log(`[API] Exchanging ${provider} token at endpoint:`, url);

  // Use the same fetchWithTimeout helper for consistency and robustness
  const fetchWithTimeout = async (url, options, timeout = 10000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(id);
      return response;
    } catch (error) {
      clearTimeout(id);
      throw error;
    }
  };

  try {
    // Add retry logic for critical auth endpoint
    let attempts = 0;
    const maxAttempts = 2; // Fewer attempts for token exchange
    let lastError = null;
    let currentUrl = url;

    while (attempts < maxAttempts) {
      try {
        const payload = {
          provider,
          jwt: token,
          token,
          ...extra,
        };
        const res = await fetchWithTimeout(currentUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Origin': window.location.origin,
            'Accept': 'application/json',
          },
          body: JSON.stringify(payload),
          mode: 'cors',
          cache: 'no-store' // Prevent any caching of auth requests
          // Note: credentials omitted intentionally as it may cause CORS issues with some third-party auth flows
        }, 8000);

        if (!res.ok) {
          const errorText = await res.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch (e) {
            errorData = { message: errorText || 'Unknown error' };
          }
          console.error(`[API] Token exchange failed (${res.status}):`, errorData);
          throw new Error(errorData.message || `Failed to exchange ${provider} token (${res.status})`);
        }

        const data = await res.json();
        console.log(`[API] Successfully exchanged ${provider} token`);
        
        // Store the API key and wallet address via AuthService
        if (data.api_key) {
          authService.setToken(data.api_key);
        }
        if (data.wallet) {
          authService.setAuthWallet(data.wallet);
        }

        return data;
      } catch (error) {
        lastError = error;
        attempts++;

        // Only retry on network errors
        if (error.name === 'TypeError' || error.name === 'AbortError') {
          console.warn(`[API] Network error during token exchange (attempt ${attempts}/${maxAttempts}):`, error.message);

          if (attempts < maxAttempts) {
            // Wait before retrying
            await new Promise(resolve => setTimeout(resolve, 1000 * attempts));

            // If we're having network issues, try the direct URL on retry
            if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
              console.log('[API] Retrying with direct DigitalOcean URL');
              currentUrl = 'https://lloan-shark.thedopechainwars.com' + exchangePath;
            }
          }
        } else {
          // For other types of errors, don't retry
          break;
        }
      }
    }

    // If we exhausted all attempts
    throw lastError || new Error(`Failed to exchange ${provider} token after multiple attempts`);
  } catch (error) {
    console.error(`[API] Error exchanging ${provider} token:`, error);
    throw error;
  }
}

// Chat function - uses POST request (not SSE)
export async function sendChatMessage({ url, message = "Hello, I'd like to talk", agent = "vinnie", player_id }) {
  try {
    // Always use the proper JWT token from authentication
    const apiKey = getApiKey();
    console.log(`[API] ✅ sendChatMessage - using API key: ${apiKey ? apiKey.substring(0, 20) + '...' : 'NONE'}`);
    console.log(`[API] ✅ sendChatMessage - localStorage dcw_api_key: ${localStorage.getItem('dcw_api_key') ? 'EXISTS' : 'MISSING'}`);
    console.log(`[API] ✅ sendChatMessage - localStorage dcw_chat_api_key: ${localStorage.getItem('dcw_chat_api_key') ? 'EXISTS' : 'MISSING'}`);

    if (!apiKey) {
      console.error('[API] No API key available for chat - authentication required');
      throw new Error('No API key available for chat');
    }

    // Make sure we have a valid URL to avoid "Cannot read properties of undefined" error
    if (!url) {
      // Default to canonical chat endpoint if URL is not provided
      url = '/api/v1/chat';
      console.log(`[API] No chat URL provided, using default: ${url}`);
    }

    // Get player_id from auth wallet if not provided
    const finalPlayerId = player_id || getAuthWallet();
    
    const fullUrl = buildApiUrl(url);
    console.log(`[API] Sending chat message to ${fullUrl}`);
    console.log(`[API] Message: "${message}"`);
    console.log(`[API] Agent: "${agent}"`);
    console.log(`[API] Player ID: "${finalPlayerId}"`);
    console.log(`[API] API Key available: ${!!apiKey}`);

    const response = await fetch(fullUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        message: message,
        character: agent,
        player_id: finalPlayerId
      }),
      mode: 'cors',
      credentials: 'include'
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(`HTTP ${response.status}: ${errorData.detail || 'Unknown error'}`);
    }

    const data = await response.json();
    console.log(`[API] Chat response received:`, data);
    console.log(`[API] Response text: "${data.response}"`);
    
    // Update game state with player data from chat response
    if (data.cash !== undefined || data.debt !== undefined || data.inventory !== undefined) {
      try {
        const gs = useGameState.getState();
        if (typeof data.cash === 'number') {
          gs.setCash(data.cash);
          console.log(`[API] Updated playerCash to ${data.cash}`);
        }
        if (data.inventory && typeof data.inventory === 'object') {
          console.log(`[API] Received inventory:`, data.inventory);
        }
        if (data.borough) {
          gs.setCurrentBorough(data.borough);
          console.log(`[API] Updated currentBorough to ${data.borough}`);
        }
        if (typeof data.reputation === 'number') {
          gs.setReputation(data.reputation);
          console.log(`[API] Updated reputation to ${data.reputation}`);
        }
      } catch (stateError) {
        console.warn('[API] Failed to update game state from chat response:', stateError);
      }
    }
    
    return data;
  } catch (error) {
    console.error('[API] Error sending chat message:', error);
    throw error;
  }
}

// Legacy function for backward compatibility - now uses POST instead of SSE
export async function streamChatSse({ url, onMessage, onError, onComplete, onDone, message = "Hello, I'd like to talk", agent = "vinnie", character, body, endpoint, onToken, player_id }) {
  try {
    console.log('[API] streamChatSse called - redirecting to sendChatMessage');

    // Handle different parameter formats from frontend
    console.log('[API] streamChatSse - received params:', { message, body, agent, character });

    // If message is the default value, try to get it from body.message
    const actualMessage = (message === "Hello, I'd like to talk" && body && body.message) ? body.message : message;
    const finalMessage = actualMessage || (body && body.message) || (typeof body === 'string' ? body : "Hello, I'd like to talk");
    const finalAgent = agent || character || (body && body.character) || (body && body.agent_name) || "vinnie";
    const finalUrl = url || endpoint || '/api/v1/chat';

    // Get player_id if not provided
    const finalPlayerId = player_id || (body && body.player_id) || getAuthWallet();

    console.log(`[API] streamChatSse - final extracted values:`, {
      finalMessage,
      finalAgent,
      finalUrl,
      finalPlayerId,
      bodyType: typeof body,
      hasBodyMessage: !!(body && body.message)
    });

    const result = await sendChatMessage({
      url: finalUrl,
      message: finalMessage,
      agent: finalAgent,
      player_id: finalPlayerId
    });

    console.log(`[API] streamChatSse - result received:`, result);

    // Simulate streaming by sending the response in chunks
    console.log('[API] streamChatSse - simulating streaming for response:', result.response);
    console.log('[API] streamChatSse - onToken available:', !!onToken);
    if (onToken && result.response) {
      const responseText = result.response;
      console.log('[API] streamChatSse - responseText:', responseText);

      // Split response into smaller chunks for simulated streaming
      const chunkSize = 3; // characters per chunk (smaller for more natural feel)
      let currentIndex = 0;
      let currentText = '';

      while (currentIndex < responseText.length) {
        const nextIndex = Math.min(currentIndex + chunkSize, responseText.length);
        const newChunk = responseText.slice(currentIndex, nextIndex);
        console.log(`[API] streamChatSse - sending chunk ${currentIndex}-${nextIndex}: "${newChunk}"`);
        currentText += newChunk;

        await new Promise(resolve => setTimeout(resolve, 50)); // Small delay between chunks
        console.log(`[API] streamChatSse - calling onToken with: "${currentText}"`);
        onToken(currentText); // Send accumulated text so far
        console.log(`[API] streamChatSse - onToken called successfully`);
        currentIndex = nextIndex;
      }
    }

    if (onMessage) {
      onMessage(result);
    }

    // Support both onComplete and onDone callback names for backwards compatibility
    if (onComplete) {
      onComplete(result); // Pass result to onComplete so quick_replies can be processed
    }
    if (onDone) {
      onDone(result); // Also support onDone callback name used by chat.js
    }

    return result;
  } catch (error) {
    console.error('[API] Error in chat:', error);
    if (onError) onError(error);
    throw error;
  }
}

// Fetch borough economy data
export async function fetchBoroughEconomy(borough) {
  try {
    const url = buildApiUrl(`/api/v1/economy/borough/${borough}`);
    const response = await authFetchWithRetry(url);
    return await response.json();
  } catch (error) {
    console.error(`[API] Error fetching borough economy data for ${borough}:`, error);
    throw error;
  }
}

// Fetch monthly pot data
export async function fetchMonthlyPotData() {
  try {
    const url = buildApiUrl('/api/v1/economy/monthly-pot');
    const response = await authFetchWithRetry(url);
    return await response.json();
  } catch (error) {
    console.error('[API] Error fetching monthly pot data:', error);
    // Return fallback data instead of throwing to prevent UI errors
    return {
      status: "ok",
      current_pot: 125000.0,
      player_contribution: 2500.0,
      distribution_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      last_contribution: new Date().toISOString(),
      pot_history: [
        {
          month: new Date().toISOString().substring(0, 7),
          total: 120000.0,
          distributed: 108000.0,
          winners: 15,
        }
      ],
      contribution_breakdown: {
        game_sessions: 45000.0,
        trading_fees: 35000.0,
        loan_interest: 25000.0,
        character_interactions: 20000.0,
      },
      next_distribution: {
        date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        estimated_amount: 112500.0,
        eligible_players: 89,
      },
    };
  }
}

// Fetch agent performance data
export async function fetchAgentPerformance(agent) {
  try {
    const url = buildApiUrl(`/api/v1/agent/${agent}/performance`);
    const response = await authFetchWithRetry(url);
    return await response.json();
  } catch (error) {
    console.error(`[API] Error fetching agent performance for ${agent}:`, error);
    throw error;
  }
}

// Fetch player stats
export async function fetchPlayerStats(walletAddress) {
  try {
    const url = buildApiUrl(`/api/v1/player/${walletAddress}/stats`);
    const response = await authFetchWithRetry(url);
    return await response.json();
  } catch (error) {
    console.error(`[API] Error fetching player stats for ${walletAddress}:`, error);
    throw error;
  }
}

export async function fetchPublicJson(url, options = {}, retries = 2) {
  let lastError;
  for (let i = 0; i < retries; i += 1) {
    try {
      const response = await fetch(url, {
        ...options,
        mode: 'cors',
        cache: 'no-cache',
        headers: {
          Accept: 'application/json',
          ...(options.headers || {}),
        },
      });
      if (!response.ok) {
        throw new Error(`Public request failed: HTTP ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
}

// Fetch market ticker data
export async function fetchMarketTicker() {
  try {
    const url = buildApiUrl('/api/v1/economy/ticker');
    return await fetchPublicJson(url, {}, 2);
  } catch (error) {
    console.error('[API] Error fetching market ticker data:', error);
    throw error;
  }
}

// Fetch chat limits
export async function fetchChatLimits() {
  try {
    // Use the correct API path with proper CORS configuration
    const url = buildApiUrl('/api/v1/chat/limits');
    const response = await authFetchWithRetry(url);
    return await response.json();
  } catch (error) {
    console.error('[API] Error fetching chat limits:', error);
    throw error;
  }
}

// List all available game agents
export async function listGameAgents() {
  try {
    const url = buildApiUrl('/api/v1/agent/list');
    const response = await authFetchWithRetry(url);
    return await response.json();
  } catch (error) {
    console.error('[API] Error listing game agents:', error);
    throw error;
  }
}

// Start economic simulation
export async function startSimulation(agent, params = {}) {
  try {
    const url = buildApiUrl('/api/v1/economy/simulation/start');
    const response = await authFetchWithRetry(url, {
      method: 'POST',
      body: JSON.stringify({
        agent,
        ...params
      }),
    });
    return await response.json();
  } catch (error) {
    console.error('[API] Error starting simulation:', error);
    throw error;
  }
}

// Get simulation status
export async function getSimulationStatus(simulationId) {
  try {
    const url = buildApiUrl(`/api/v1/economy/simulation/status?id=${simulationId}`);
    const response = await authFetchWithRetry(url);
    return await response.json();
  } catch (error) {
    console.error(`[API] Error getting simulation status for ${simulationId}:`, error);
    throw error;
  }
}

/**
 * Trigger an agent to respond in a conversation
 * @param {string} agentId - ID of the agent to trigger
 * @param {string} conversationId - ID of the conversation
 * @param {Object} options - Additional options for the agent
 * @returns {Promise<Object>} - Response from the agent
 */
export async function _triggerAgent(agentId, conversationId, options = {}) {
  try {
    console.log(`[API] Triggering agent ${agentId} in conversation ${conversationId}`);
    const url = buildApiUrl(`/chat/agent/${agentId}/trigger`);
    const response = await authFetchWithRetry(url, {
      method: 'POST',
      body: JSON.stringify({
        conversation_id: conversationId,
        ...options
      }),
    });
    return await response.json();
  } catch (error) {
    console.error(`[API] Error triggering agent ${agentId}:`, error);
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Game API helpers (backwards-compat shim) ----------------------------------
// These moved to `src/utils/gameApi.js`.
// Re-export to avoid touching every call-site while preserving the new file structure.
export {
  getFirstEncounter,
  getPlayerStats,
  getLeaderboard
} from './gameApi';
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Smart Wallet helper — sendSponsoredTx
// ---------------------------------------------------------------------------
/**
 * Send a transaction, using smartAccount.sponsorTransaction when available and
 * falling back to personal eth_sendTransaction otherwise. Designed purely for
 * frontend convenience — no credentials header changes.
 *
 * @param {Object} tx – Ethers transaction request (to, data, value, etc.)
 * @returns {Promise<string>} Transaction hash
 */
export async function sendSponsoredTx(tx) {
  // 1) Preferred path – Smart Wallet injected helper
  if (typeof window !== 'undefined' && window.__DCW_SMART__ && typeof window.__DCW_SMART__.sponsorTransaction === 'function') {
    const res = await window.__DCW_SMART__.sponsorTransaction(tx);
    return res?.transactionHash || res?.hash || '';
  }

  // 2) Fallback – EOA provider via window.ethereum
  if (typeof window !== 'undefined' && window.ethereum && typeof window.ethereum.request === 'function') {
    // Ensure we have the account selected
    const [from] = await window.ethereum.request({ method: 'eth_accounts' });
    if (!from) throw new Error('No wallet connected');

    const txRequest = { from, ...tx };
    return await window.ethereum.request({ method: 'eth_sendTransaction', params: [txRequest] });
  }

  throw new Error('No transaction provider available');
}
// ---------------------------------------------------------------------------
// API Key validation helper
// ---------------------------------------------------------------------------
/**
 * Check if the current API key is valid by making a test request
 * @returns {Promise<boolean>} - True if API key is valid, false otherwise
 */
export async function validateApiKey() {
  try {
    const apiKey = getApiKey();
    if (!apiKey) {
      console.log('[API] No API key to validate');
      return false;
    }

    const url = buildApiUrl('/auth/me/api');
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${apiKey}`,
        'Content-Type': 'application/json',
      },
      mode: 'cors',
      credentials: 'include',
    });

    if (response.status === 200) {
      console.log('[API] API key validation successful');
      return true;
    } else {
      console.log(`[API] API key validation failed with status: ${response.status}`);
      return false;
    }
  } catch (error) {
    console.error('[API] Error validating API key:', error);
    return false;
  }
}

// ---------------------------------------------------------------------------
// Force re-authentication helper
// ---------------------------------------------------------------------------
/**
 * Force the user to re-authenticate by clearing all auth data and triggering login
 */
export function forceReAuthentication() {
  console.log('[API] Forcing re-authentication...');

  // Clear all authentication data
  clearAuth();

  // Clear any additional auth-related localStorage items
  if (typeof window !== 'undefined') {
    localStorage.removeItem('dcw_access_token');
    localStorage.removeItem('dcw_refresh_token');
    localStorage.removeItem('dcw_chat_api_key');
    localStorage.removeItem('dcw_auth_wallet');

    // Dispatch events to notify the UI
    window.dispatchEvent(new CustomEvent('dcw-auth-invalid', {
      detail: {
        reason: 'forced_reauth',
        message: 'Please log in again to continue.'
      }
    }));

    window.dispatchEvent(new CustomEvent('dcw-force-login'));
  }
}

// ---------------------------------------------------------------------------
// Enhanced error handling for auth failures
// ---------------------------------------------------------------------------
/**
 * Enhanced error handler for authentication failures
 * @param {Error} error - The error that occurred
 * @param {string} context - Context where the error occurred
 */

/**
 * Remove only obsolete localStorage keys on load. Do NOT remove dcw_refresh_token
 * or dcw_access_token—they are required for the 401 refresh flow. Previously
 * removing them caused Base/desktop to constantly ask for new signatures.
 */
(function cleanupLegacyTokens() {
  if (typeof window === 'undefined') return;
  const legacyKeys = ['jwt', 'exp'];
  legacyKeys.forEach(key => {
    if (localStorage.getItem(key)) {
      console.log(`[API] Cleaning up legacy key: ${key}`);
      localStorage.removeItem(key);
    }
  });
})();
export function handleAuthError(error, context = 'unknown') {
  console.error(`[API] Auth error in ${context}:`, error);

  // Check if it's an authentication error
  if (error.message && (
    error.message.includes('401') ||
    error.message.includes('Unauthorized') ||
    error.message.includes('Invalid token') ||
    error.message.includes('No API key')
  )) {
    console.log(`[API] Authentication error detected in ${context}, triggering re-auth`);
    forceReAuthentication();
    return true; // Error was handled
  }

  return false; // Error was not handled
}
